import type { VideoFrameV2 } from '@mimir/cutter/frameProvider/frameProvider';
import type { ShaderVideoFrame, ShaderTextureSourceSystemMemory } from './shaderContext';

/**
 *  If this function returns null it means the pixel format of the video frame
 *  is not supported.
 */
export function shaderVideoFrameFromVideoFrameV2(videoFrame: VideoFrameV2): ShaderVideoFrame | null {
  return shaderVideoFrameFromVideoFrameV2Internal(videoFrame);
}

// end of public API.

interface YUV420Data {
  lumaWidth: number;
  lumaHeight: number;
  lumaBytes: Uint8Array,
  chromaWidth: number;
  chromaHeight: number;
  cbBytes: Uint8Array,
  crBytes: Uint8Array,
}

function calculateYUV420Data(resolution: { width: number, height: number }, bytes: Uint8Array): YUV420Data {
  const lumaWidth = resolution.width;
  const lumaHeight = resolution.height;
  const lumaByteCount = lumaWidth * lumaHeight;

  const chromaWidth = lumaWidth/ 2;
  const chromaHeight = lumaHeight / 2;
  const chromaByteCount = chromaWidth * chromaHeight;

  let offset = 0;
  const lumaBytes = bytes.subarray(offset, offset + lumaByteCount);
  offset += lumaByteCount;
  const cbBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;
  const crBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;

  return {
    lumaWidth,
    lumaHeight,
    lumaBytes,
    chromaWidth,
    chromaHeight,
    crBytes,
    cbBytes,
  };
}

interface NV12Data {
  lumaWidth: number;
  lumaHeight: number;
  lumaBytes: Uint8Array,
  chromaWidth: number;
  chromaHeight: number;
  cbcrBytes: Uint8Array,
}

function calculateNV12Data(resolution: { width: number, height: number }, bytes: Uint8Array): NV12Data {
  const lumaWidth = resolution.width;
  const lumaHeight = resolution.height;
  const lumaByteCount = lumaWidth * lumaHeight;

  const chromaWidth = lumaWidth/ 2;
  const chromaHeight = lumaHeight / 2;
  const chromaByteCount = chromaWidth * chromaHeight * 2;

  let offset = 0;
  const lumaBytes = bytes.subarray(offset, offset + lumaByteCount);
  offset += lumaByteCount;
  const cbcrBytes = bytes.subarray(offset, offset + chromaByteCount);
  offset += chromaByteCount;

  return {
    lumaWidth,
    lumaHeight,
    lumaBytes,
    chromaWidth,
    chromaHeight,
    cbcrBytes,
  };
}

function shaderVideoFrameFromVideoFrameV2Internal(videoFrame: VideoFrameV2): ShaderVideoFrame | null {
  const data = new Uint8Array(videoFrame.buffer.size());
  videoFrame.buffer.copyTo(data);
  switch (videoFrame.pixelFormat) {
    case 'bgr0': {
      const bgr0LinearTexture : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        // This is not a mistake. The GPU has no concept of bgra textures.
        // Since the alpha is in the same location as rgba we can use the
        // srgb8_alpha8 format and shuffle the RGB components in the shader.
        format: 'srgb8_alpha8',
        width: videoFrame.resolution.width,
        height: videoFrame.resolution.height,
        data,
      };
      return {
        interface: 'bgr0_linear_to_rgba_linear',
        bgr0LinearTexture,
      }
    }
    case 'bgra': {
      const bgraLinearTexture : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        // This is not a mistake. The GPU has no concept of bgra textures.
        // Since the alpha is in the same location as rgba we can use the
        // srgb8_alpha8 format and shuffle the RGB components in the shader.
        format: 'srgb8_alpha8',
        width: videoFrame.resolution.width,
        height: videoFrame.resolution.height,
        data,
      };
      return {
        interface: 'bgra_linear_to_rgba_linear',
        bgraLinearTexture,
      }
    }
    case 'rgba': {
      const rgbaLinearTexture : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'srgb8_alpha8',
        width: videoFrame.resolution.width,
        height: videoFrame.resolution.height,
        data,
      };
      return {
        interface: 'rgba_linear_to_rgba_linear',
        rgbaLinearTexture,
      }
    }
    case 'yuv420p': {
      const yuv420Data =
        calculateYUV420Data(videoFrame.resolution, data);

      const luma : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'r8',
        width: yuv420Data.lumaWidth,
        height: yuv420Data.lumaHeight,
        data: yuv420Data.lumaBytes,
      };

      const cb : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'r8',
        width: yuv420Data.chromaWidth,
        height: yuv420Data.chromaHeight,
        data: yuv420Data.cbBytes,
      };

      const cr : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'r8',
        width: yuv420Data.chromaWidth,
        height: yuv420Data.chromaHeight,
        data: yuv420Data.crBytes,
      };

      return { interface: 'yuv_planar_bt709_to_rgba_linear', luma, cb, cr, };
    }
    case 'nv12': {
      const nv12Data = calculateNV12Data(videoFrame.resolution, data);

      const luma : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'r8',
        width: nv12Data.lumaWidth,
        height: nv12Data.lumaHeight,
        data: nv12Data.lumaBytes,
      };

      const cbcr : ShaderTextureSourceSystemMemory = {
        type: 'system_memory',
        format: 'rg8',
        width: nv12Data.chromaWidth,
        height: nv12Data.chromaHeight,
        data: nv12Data.cbcrBytes,
      };

      return { interface: 'nv12_bt709_to_rgba_linear', luma, cbcr };
    }
  }
  return null;
}
